import { Link2 } from 'lucide-react'
import { ProcessLinkPicker } from './ProcessLinkPicker'
import type { ProcessListItem } from '../../../occurrences/types/occurrences'

interface ProcessLinkFieldProps {
  /** Processo detectado pelo contexto (página de processo, aba HUB). */
  relatedProcessId: number | null
  /** Rótulo já resolvido (`process_number`) — `null` enquanto carrega. */
  processLabel: string | null
  /** Vínculo obrigatório (aba HUB): vira aviso fixo, sem checkbox. */
  locked: boolean
  linkProcess: boolean
  onLinkProcessChange: (value: boolean) => void
  /** Escolha manual — só quando não há processo de contexto. */
  manualProcess: ProcessListItem | null
  onManualProcessChange: (process: ProcessListItem | null) => void
  /** Usuário sem acesso a Ocorrências não recebe o seletor manual. */
  canPickProcess: boolean
}

const BANNER_CLASS =
  'flex items-start gap-2.5 rounded-[5px] border border-[#bfdbfe] dark:border-blue-700 bg-[#eff6ff] dark:bg-blue-900/20 px-3 py-2.5 text-xs text-[#2563eb] dark:text-blue-200'

/** Frase repetida nos três estados — o vínculo é referência E visibilidade. */
const LINK_EFFECT_TEXT =
  'O problema continua no "Atendimento HUB" e passa a aparecer também na aba "HUB" desse processo.'

/**
 * Campo de vínculo com processo do formulário de novo problema, nos três
 * modos possíveis: travado (aba HUB), sugerido por contexto (checkbox) e
 * escolha manual (picker).
 */
export function ProcessLinkField({
  relatedProcessId,
  processLabel,
  locked,
  linkProcess,
  onLinkProcessChange,
  manualProcess,
  onManualProcessChange,
  canPickProcess,
}: ProcessLinkFieldProps) {
  const label = processLabel ?? `#${relatedProcessId}`

  if (relatedProcessId && locked) {
    return (
      <p className={BANNER_CLASS}>
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          <strong>Vinculado ao processo {label}</strong>
          <br />
          Aberto pela aba HUB do processo — o vínculo é automático e o problema
          fica visível para o time do centro de custo dele.
        </span>
      </p>
    )
  }

  if (relatedProcessId) {
    return (
      <label className={BANNER_CLASS}>
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <input
          type="checkbox"
          checked={linkProcess}
          onChange={(e) => onLinkProcessChange(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <strong>Vincular ao processo {label}?</strong>
          <br />
          Marque pra facilitar localizar depois — {LINK_EFFECT_TEXT}
        </span>
      </label>
    )
  }

  if (!canPickProcess) return null

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-body/80">
        Vincular a um processo (opcional)
      </label>
      <ProcessLinkPicker
        selected={manualProcess}
        onSelect={(p) => {
          onManualProcessChange(p)
          onLinkProcessChange(true)
        }}
        onClear={() => {
          onManualProcessChange(null)
          onLinkProcessChange(false)
        }}
      />
      {manualProcess && (
        <p className="mt-1 text-[11px] text-subtle">{LINK_EFFECT_TEXT}</p>
      )}
    </div>
  )
}
